/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import Pagination from '../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import { 
  MLTTestRecord, 
  INITIAL_MLT_RECORDS, 
  SAMPLE_SUBTYPES_MAP 
} from './data/mltBioburdenData';

const MLTBioburdenTests: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Master records state
  const [records, setRecords] = useState<MLTTestRecord[]>(INITIAL_MLT_RECORDS);
  const [toastMessage, setToastMessage] = useState<{ title: string; desc: string; type: 'info' | 'success' | 'warning' } | null>(null);

  // Search & Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSampleType, setSelectedSampleType] = useState('All');
  const [selectedSampleSubtype, setSelectedSampleSubtype] = useState('All');
  const [selectedTestMethod, setSelectedTestMethod] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedOperator, setSelectedOperator] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Report Ineligibility Modal State
  const [unavailableReportRecord, setUnavailableReportRecord] = useState<MLTTestRecord | null>(null);

  const showToast = (title: string, desc: string, type: 'info' | 'success' | 'warning' = 'info') => {
    setToastMessage({ title, desc, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Reset Subtype when Sample Type changes
  const handleSampleTypeFilterChange = (type: string) => {
    setSelectedSampleType(type);
    setSelectedSampleSubtype('All');
    setPage(1);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedSampleType('All');
    setSelectedSampleSubtype('All');
    setSelectedTestMethod('All');
    setSelectedStatus('All');
    setSelectedOperator('All');
    setDateFrom('');
    setDateTo('');
    setPage(1);
    showToast('Filters Cleared', 'All search criteria have been reset to default.', 'info');
  };

  // Operators list derived from records
  const operatorOptions = useMemo(() => {
    const set = new Set<string>();
    records.forEach(r => {
      if (r.assignedOperator) set.add(r.assignedOperator);
    });
    return Array.from(set).sort();
  }, [records]);

  // Dependent Subtypes list based on selected filter
  const currentSubtypeOptions = useMemo(() => {
    if (selectedSampleType === 'All') {
      const allSubtypes = Object.values(SAMPLE_SUBTYPES_MAP).flat();
      return Array.from(new Set(allSubtypes)).sort();
    }
    return SAMPLE_SUBTYPES_MAP[selectedSampleType] || [];
  }, [selectedSampleType]);

  // Filtering records
  const filteredRecords = useMemo(() => {
    return records.filter((rec) => {
      // Free text search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesQuery = 
          rec.id.toLowerCase().includes(q) ||
          rec.taskId.toLowerCase().includes(q) ||
          rec.sampleId.toLowerCase().includes(q) ||
          rec.sampleName.toLowerCase().includes(q) ||
          rec.batchNo.toLowerCase().includes(q) ||
          (rec.productionSchedule && rec.productionSchedule.toLowerCase().includes(q));
        if (!matchesQuery) return false;
      }

      // Sample Type
      if (selectedSampleType !== 'All' && rec.sampleType !== selectedSampleType) {
        return false;
      }

      // Sample Subtype
      if (selectedSampleSubtype !== 'All' && rec.sampleSubtype !== selectedSampleSubtype) {
        return false;
      }

      // Test Method
      if (selectedTestMethod !== 'All' && rec.testMethod !== selectedTestMethod) {
        return false;
      }

      // Status
      if (selectedStatus !== 'All' && rec.status !== selectedStatus) {
        return false;
      }

      // Assigned Operator
      if (selectedOperator !== 'All' && rec.assignedOperator !== selectedOperator) {
        return false;
      }

      return true;
    });
  }, [
    records,
    searchQuery,
    selectedSampleType,
    selectedSampleSubtype,
    selectedTestMethod,
    selectedStatus,
    selectedOperator
  ]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  //handle table pagination
  const handlePageChange = (newPage: any, newPageSize?: number) => {
    setPage(newPage);
    if (newPageSize) {
      setPageSize(newPageSize);
    }
  };

  // Action Click Handlers
  const handleViewReportClick = (rec: MLTTestRecord) => {
    if (rec.reportAvailable) {
      navigate(`/mltBioburdenTests/report/${rec.id}`, { state: { record: rec } });
    } else {
      setUnavailableReportRecord(rec);
    }
  };

  const handleViewDetailsClick = (rec: MLTTestRecord) => {
    navigate(`/mltBioburdenTests/report/${rec.id}`, { state: { record: rec } });
  };

  const getStatusBadge = (status: MLTTestRecord['status']) => {
    switch (status) {
      case 'Pending Initiation':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'In Progress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Pending Lab Approval':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Pending QA Approval':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'Completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Rejected':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'Cancelled':
        return 'bg-slate-100 text-slate-500 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  return (
    <div>
      <HeaderSection />
      <PageHeader pageHeader={t('navbar.Reports.MLT Bioburden Tests')} pageDescription={t('mltBioburdenTests.header.subtitle')} />

      <div className="min-h-screen bg-[#F8FAFC] pb-16">
        {/* Toast Alert */}
        <AnimatePresence>
          {toastMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`fixed top-4 right-6 z-50 flex items-start gap-3 p-4 rounded-xl border shadow-xl max-w-md ${
                toastMessage.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : toastMessage.type === 'warning'
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-blue-50 border-blue-200 text-blue-900'
              }`}
            >
              <span className="material-symbols-outlined text-xl">
                {toastMessage.type === 'success' ? 'check_circle' : toastMessage.type === 'warning' ? 'warning' : 'info'}
              </span>
              <div className="flex-1">
                <h4 className="text-xs font-black uppercase tracking-wider">{toastMessage.title}</h4>
                <p className="text-xs mt-0.5 text-slate-600 font-medium">{toastMessage.desc}</p>
              </div>
              <button onClick={() => setToastMessage(null)} className="text-slate-400 hover:text-slate-600 text-sm">
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

      {/* Main Container */}
      <main className="max-w-[1720px] mx-auto px-6 pt-6 space-y-6">

        {/* SECTION: Filters */}
        <section className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-8 bg-slate-50/50 border-b border-slate-50">
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-6">
              <div className="col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Search</label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                  <input
                    type="text"
                    placeholder="Search by ID, name, batch..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0052CC]/20 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Sample Type</label>
                <select
                  value={selectedSampleType}
                  // onChange={handleSampleTypeFilterChange}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                >
                  <option value="All">All Sample Types</option>
                  <option value="Finished Product">Finished Product</option>
                  <option value="Raw Material">Raw Material</option>
                  <option value="In-Process Bulk">In-Process Bulk</option>
                  <option value="Water for Injection (WFI)">Water for Injection (WFI)</option>
                  <option value="Purified Water">Purified Water</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Sample Subtype</label>
                <select
                  value={selectedSampleSubtype}
                  onChange={(e) => { setSelectedSampleSubtype(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                >
                  <option value="All">All Subtypes</option>
                  {currentSubtypeOptions.map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Test Method</label>
                <select
                  value={selectedTestMethod}
                  onChange={(e) => { setSelectedTestMethod(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                >
                  <option value="All">All Test Methods</option>
                  <option value="Filtration">Filtration</option>
                  <option value="Plate Count">Plate Count</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                >
                  <option value="All">All Statuses</option>
                  <option value="Pending Initiation">Pending Initiation</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Pending Lab Approval">Pending Lab Approval</option>
                  <option value="Pending QA Approval">Pending QA Approval</option>
                  <option value="Completed">Completed</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Operator</label>
                <select
                  value={selectedOperator}
                  onChange={(e) => { setSelectedOperator(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                >
                  <option value="All">All Operators</option>
                  {operatorOptions.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">From Date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">To Date</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs outline-none"
                />
              </div>
              <div className="flex items-end gap-3">
                <button
                  className="px-3 bg-white border border-slate-200 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-50 transition-colors"
                  onClick={handleClearFilters}
                >
                  <span className="material-symbols-outlined text-sm">restart_alt</span>
                </button>
              </div>
            </div>
            </div>
          </section>

          {/* SECTION: Listing Table */}
          <section className="bg-white shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto min-h-[400px] max-h-[400px]">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="tableHeadRow text-center">
                    <th className="tableHeadData2">S. No.</th>
                    <th className="tableHeadData2">Task ID</th>
                    <th className="tableHeadData2">Test ID</th>
                    <th className="tableHeadData2">Sample Type</th>
                    <th className="tableHeadData2">Sample / Product Name</th>
                    <th className="tableHeadData2">Batch / Lot No.</th>
                    <th className="tableHeadData2">Test Method</th>
                    <th className="tableHeadData2">Required Plates</th>
                    <th className="tableHeadData2">Assigned Operator</th>
                    <th className="tableHeadData2">Created Date</th>
                    <th className="tableHeadData2">Initiated Date</th>
                    <th className="tableHeadData2">Current Stage</th>
                    <th className="tableHeadData2">Status</th>
                    <th className="tableHeadData2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {paginatedRecords.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center gap-3">
                          <span className="material-symbols-outlined text-4xl text-slate-300">inbox</span>
                          <p className="text-sm text-slate-500">No data available</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedRecords.map((rec, index) => {
                      const rowNumber = (page - 1) * pageSize + index + 1;

                      return (
                        <tr 
                          key={rec.id} 
                          className="hover:bg-slate-50/50 transition-colors"
                        >
                          {/* S. No. */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-500">
                            {rowNumber}
                          </td>

                          {/* Task ID */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-800">
                            {rec.taskId}
                          </td>

                          {/* Test ID */}
                          <td className="px-6 py-4 text-xs font-bold text-[#0052CC]">
                            {rec.id}
                          </td>

                          {/* Sample Type */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-800">
                            {rec.sampleType}
                          </td>

                          {/* Sample / Product Name */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-800">
                            {rec.sampleName}
                          </td>

                          {/* Batch / Lot No. */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-600">
                            {rec.batchNo}
                          </td>

                          {/* Test Method */}
                          <td className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase">
                            {rec.testMethod}
                          </td>

                          {/* Required Plates */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-600">
                            {rec.requiredPlates}
                          </td>

                          {/* Assigned Operator */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-600">
                            {rec.assignedOperator}
                          </td>

                          {/* Created Date */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-600">
                            {rec.createdDate}
                          </td>

                          {/* Initiated Date */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-600">
                            {rec.initiatedDate || '-'}
                          </td>

                          {/* Current Stage */}
                          <td className="px-6 py-4 text-xs font-bold text-slate-600">
                            {rec.currentStage}
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4">
                            <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest ${getStatusBadge(rec.status)}`}>
                              {rec.status}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleViewDetailsClick(rec)}
                              className="text-[10px] font-black text-[#0052CC] uppercase tracking-widest hover:underline"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />

        </section>
        </main>
      </div>

      {/* MODAL: Report Ineligibility Info Dialog */}
      <AnimatePresence>
        {unavailableReportRecord && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full overflow-hidden"
            >
              <div className="bg-amber-600 text-white p-4 flex items-center gap-3">
                <span className="material-symbols-outlined text-2xl">pending</span>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider">Analytical Report Not Yet Ready</h3>
                  <p className="text-[10px] text-amber-100">Test data collection in progress</p>
                </div>
              </div>
              <div className="p-5 space-y-3 text-xs">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5 text-slate-700">
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">Test ID:</span>
                    <span className="font-mono font-bold text-blue-700">{unavailableReportRecord.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">Current Status:</span>
                    <span className="font-bold text-amber-700">{unavailableReportRecord.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">Current Stage:</span>
                    <span className="font-bold text-slate-800">{unavailableReportRecord.currentStage}</span>
                  </div>
                </div>

                <p className="text-slate-600 leading-relaxed font-medium">
                  {unavailableReportRecord.reportUnavailableReason || 
                   'The analytical report requires completed incubation observations, colony enumeration (TAC/TFC), and electronic review sign-offs before generating a finalized certificate.'}
                </p>
              </div>
              <div className="p-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setUnavailableReportRecord(null)}
                  className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition-all"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MLTBioburdenTests;
